import VALIDATION_MESSAGES, { REGEX_PATTERNS } from './validationMessages';

export interface DocumentValidationResult {
  isValid: boolean;
  error?: string;
}
export const formatPanNumber = (value: string): string => {
  if (!value) return '';
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 10);
};

export const formatAadhaarNumber = (value: string): string => {
  if (!value) return '';
  return value.replace(/\D/g, '').slice(0, 12);
};

export const formatPassportNumber = (value: string): string => {
  if (!value) return '';
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 9);
};

export const formatAccountNumber = (value: string): string => {
  if (!value) return '';
  return value.replace(/\D/g, '').slice(0, 20);
};

export const formatIfscCode = (value: string): string => {
  if (!value) return '';
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 11);
};

export const validatePanNumber = (value: string): DocumentValidationResult => {
  const formatted = formatPanNumber(value);
  if (!formatted) {
    return { isValid: false, error: VALIDATION_MESSAGES.panNumber.required };
  }
  if (!REGEX_PATTERNS.pan.test(formatted)) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.panNumber.pattern,
    };
  }
  return { isValid: true };
};

export const validateAadhaarNumber = (
  value: string
): DocumentValidationResult => {
  const formatted = formatAadhaarNumber(value);
  if (!formatted) {
    return { isValid: false, error: VALIDATION_MESSAGES.aadharNumber.required };
  }
  if (!REGEX_PATTERNS.aadhar.test(formatted)) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.aadharNumber.pattern,
    };
  }
  return { isValid: true };
};

export const validatePassportNumber = (
  value: string
): DocumentValidationResult => {
  const formatted = formatPassportNumber(value);
  if (!formatted) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.passportNumber.required,
    };
  }
  if (!REGEX_PATTERNS.passport.test(formatted)) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.passportNumber.pattern,
    };
  }
  return { isValid: true };
};

export const validateAccountNumber = (
  value: string
): DocumentValidationResult => {
  const formatted = formatAccountNumber(value);
  if (!formatted) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.accountNumber.required,
    };
  }
  if (!REGEX_PATTERNS.accountNumber.test(formatted)) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.accountNumber.pattern,
    };
  }
  return { isValid: true };
};

export const validateIfscCode = (value: string): DocumentValidationResult => {
  const formatted = formatIfscCode(value);
  if (!formatted) {
    return { isValid: false, error: VALIDATION_MESSAGES.ifscCode.required };
  }
  if (!REGEX_PATTERNS.ifsc.test(formatted)) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.ifscCode.pattern,
    };
  }
  return { isValid: true };
};

export const validateRoutingNumber = (
  value: string
): DocumentValidationResult => {
  const clean = (value || '').trim();
  if (!clean) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.routingNumber.required,
    };
  }
  if (!REGEX_PATTERNS.routingNumber.test(clean)) {
    return { isValid: false, error: VALIDATION_MESSAGES.routingNumber.pattern };
  }
  return { isValid: true };
};

export const validateSortCode = (value: string): DocumentValidationResult => {
  const clean = (value || '').trim();
  if (!clean) {
    return { isValid: false, error: VALIDATION_MESSAGES.sortCode.required };
  }
  if (!REGEX_PATTERNS.sortCode.test(clean)) {
    return { isValid: false, error: VALIDATION_MESSAGES.sortCode.pattern };
  }
  return { isValid: true };
};

export const validateBsbNumber = (value: string): DocumentValidationResult => {
  const clean = (value || '').trim();
  if (!clean) {
    return { isValid: false, error: VALIDATION_MESSAGES.bsbNumber.required };
  }
  if (!REGEX_PATTERNS.bsbNumber.test(clean)) {
    return { isValid: false, error: VALIDATION_MESSAGES.bsbNumber.pattern };
  }
  return { isValid: true };
};

export const validateIban = (value: string): DocumentValidationResult => {
  const clean = (value || '').replace(/\s/g, '').toUpperCase();
  if (!clean) {
    return { isValid: false, error: VALIDATION_MESSAGES.iban.required };
  }
  if (!REGEX_PATTERNS.iban.test(clean)) {
    return { isValid: false, error: VALIDATION_MESSAGES.iban.pattern };
  }
  return { isValid: true };
};

export const validateBicSwift = (value: string): DocumentValidationResult => {
  const clean = (value || '').trim().toUpperCase();
  if (!clean) {
    return { isValid: true }; // Optional field
  }
  if (!REGEX_PATTERNS.bicSwift.test(clean)) {
    return { isValid: false, error: VALIDATION_MESSAGES.bicSwift.pattern };
  }
  return { isValid: true };
};

export const validateInstitutionNumber = (
  value: string
): DocumentValidationResult => {
  const clean = (value || '').replace(/\s/g, '');
  if (!clean) {
    return { isValid: false, error: 'Institution number is required' };
  }
  if (!/^\d{3}$/.test(clean)) {
    return {
      isValid: false,
      error: 'Institution number must be 3 digits (e.g. 003)',
    };
  }
  return { isValid: true };
};

export const validateTransitNumber = (
  value: string
): DocumentValidationResult => {
  const clean = (value || '').replace(/\s/g, '');
  if (!clean) {
    return { isValid: false, error: 'Transit number is required' };
  }
  if (!/^\d{5}$/.test(clean)) {
    return {
      isValid: false,
      error: 'Transit number must be 5 digits (e.g. 00022)',
    };
  }
  return { isValid: true };
};

export const DOCUMENT_VALIDATORS = {
  pan: validatePanNumber,
  aadhaar: validateAadhaarNumber,
  passport: validatePassportNumber,
  accountNumber: validateAccountNumber,
  ifsc: validateIfscCode,
  routingNumber: validateRoutingNumber,
  sortCode: validateSortCode,
  bsbNumber: validateBsbNumber,
  iban: validateIban,
  bicSwift: validateBicSwift,
};
